import { Controller } from "@hotwired/stimulus"

// Drives the product detail modal:
//   - recomputes the running total as the user picks variants/modifiers
//   - handles the quantity stepper
//   - closes the modal by emptying the turbo frame
export default class extends Controller {
  static targets = ["variant", "modifier", "quantity", "totalLabel", "submit", "hint", "modifierIds"]
  static values = {
    baseCents: Number,
    currency: String,
  }

  connect() {
    this.recompute()
    this.boundKey = this.handleKey.bind(this)
    document.addEventListener("keydown", this.boundKey)
    document.body.style.overflow = "hidden"
  }

  disconnect() {
    document.removeEventListener("keydown", this.boundKey)
    document.body.style.overflow = ""
  }

  handleKey(e) {
    if (e.key === "Escape") this.close()
  }

  increment() {
    const input = this.quantityTarget
    const next = Math.min(99, (parseInt(input.value, 10) || 1) + 1)
    input.value = next
    this.recompute()
  }

  decrement() {
    const input = this.quantityTarget
    const next = Math.max(1, (parseInt(input.value, 10) || 1) - 1)
    input.value = next
    this.recompute()
  }

  recompute() {
    // The modal renders read-only when the restaurant is not taking online
    // orders: no form, so nothing to price. Closing still works.
    if (!this.hasQuantityTarget) return

    const quantity = Math.max(1, parseInt(this.quantityTarget.value, 10) || 1)

    // Variant (radio) — pick checked; fall back to base
    let unit = this.baseCentsValue
    const checkedVariant = this.variantTargets.find((t) => t.checked)
    if (checkedVariant) {
      unit = parseInt(checkedVariant.dataset.priceCents, 10) || unit
    }

    // Modifiers — sum deltas from all checked checkboxes/radios
    const delta = this.modifierTargets
      .filter((t) => t.checked)
      .reduce((sum, t) => sum + (parseInt(t.dataset.priceDeltaCents, 10) || 0), 0)

    const totalCents = (unit + delta) * quantity
    this.totalLabelTarget.textContent =
      `${this.currencyValue}${this.#format(totalCents)}`

    // Enforce per-group max_selections: once a group's checked count hits
    // its cap, disable the remaining unchecked inputs in that group so the
    // user visually understands they can't pick more.
    this.#enforceGroupCaps()
    this.#syncSelectedIds()
    this.#enforceCompleteness()
  }

  // The visible modifier inputs use ui_modifier_group_X names (so each group
  // has its own browser-radio scope). We mirror the checked ids into hidden
  // <input name="interaction[modifier_option_ids][]"> entries so Plutonium's
  // array extractor sees a proper array.
  #syncSelectedIds() {
    if (!this.hasModifierIdsTarget) return
    const container = this.modifierIdsTarget
    container.innerHTML = ""
    for (const input of this.modifierTargets) {
      if (!input.checked) continue
      const hidden = document.createElement("input")
      hidden.type = "hidden"
      hidden.name = "interaction[modifier_option_ids][]"
      hidden.value = input.value
      container.appendChild(hidden)
    }
  }

  #enforceCompleteness() {
    const missing = []

    if (this.variantTargets.length > 0 && !this.variantTargets.some((t) => t.checked)) {
      missing.push("Select an option")
    }

    const groups = new Map()
    for (const input of this.modifierTargets) {
      const gid = input.dataset.groupId
      if (!gid) continue
      if (!groups.has(gid)) groups.set(gid, [])
      groups.get(gid).push(input)
    }
    for (const inputs of groups.values()) {
      const min = parseInt(inputs[0].dataset.groupMin, 10) || 0
      if (min <= 0) continue
      const checkedCount = inputs.filter((i) => i.checked).length
      if (checkedCount < min) {
        const name = inputs[0].dataset.groupName || "Required group"
        missing.push(min === 1 ? `${name}: choose 1` : `${name}: choose at least ${min}`)
      }
    }

    if (this.hasSubmitTarget) this.submitTarget.disabled = missing.length > 0
    if (this.hasHintTarget) {
      if (missing.length === 0) {
        this.hintTarget.classList.add("hidden")
        this.hintTarget.textContent = ""
      } else {
        this.hintTarget.classList.remove("hidden")
        this.hintTarget.textContent = missing.join(" · ")
      }
    }
  }

  #enforceGroupCaps() {
    // Group modifiers by their group id (checkboxes only — radio buttons
    // already enforce mutual exclusion via the browser).
    const groups = new Map()
    for (const input of this.modifierTargets) {
      if (input.type === "radio") continue
      const gid = input.dataset.groupId
      if (!gid) continue
      if (!groups.has(gid)) groups.set(gid, [])
      groups.get(gid).push(input)
    }

    for (const inputs of groups.values()) {
      const maxAttr = inputs[0].dataset.groupMax
      const max = parseInt(maxAttr, 10)
      if (Number.isNaN(max) || max <= 0) {
        // No cap — make sure nothing is disabled by a previous pass.
        for (const input of inputs) {
          input.disabled = false
          input.closest("label")?.classList.remove("opacity-50", "cursor-not-allowed")
        }
        continue
      }

      const checkedCount = inputs.filter((i) => i.checked).length
      const atCap = checkedCount >= max

      for (const input of inputs) {
        const disable = atCap && !input.checked
        input.disabled = disable
        const label = input.closest("label")
        if (label) {
          label.classList.toggle("opacity-50", disable)
          label.classList.toggle("cursor-not-allowed", disable)
          label.classList.toggle("cursor-pointer", !disable)
        }
      }
    }
  }

  close() {
    const frame = document.getElementById("product_modal_frame")
    if (frame) frame.innerHTML = ""
  }

  #format(cents) {
    const value = cents / 100
    return value.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  }
}
